export type FooterVariant = "default" | "shop" | "checkout" | "about";

/** Routes where the global marketing footer must not render (focused app shells). */
const FOOTER_HIDDEN_EXACT = new Set([
  "/cart",
  "/checkout",
  "/design-tool",
  "/design-tool2",
  "/design-preview",
  "/preview",
  "/unauthorized",
]);

const FOOTER_HIDDEN_PREFIXES = [
  "/account", // buyer dashboard + settings
  "/order/", // checkout confirmation + post-purchase tracking
  "/design/", // immersive customize / legacy customize flows
] as const;

/**
 * Public marketing surfaces: homepage, shop/search, product PDPs, creator stores,
 * help center, about/contact, and legal pages.
 *
 * Hidden on editor, cart/checkout, auth (separate layout), and all dashboard shells.
 */
export function shouldShowFooter(pathname: string): boolean {
  if (FOOTER_HIDDEN_EXACT.has(pathname)) return false;

  return !FOOTER_HIDDEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix),
  );
}

export function resolveFooterVariant(pathname: string): FooterVariant {
  if (pathname === "/cart" || pathname === "/checkout") return "checkout";
  if (
    pathname.startsWith("/shop") ||
    pathname.startsWith("/product") ||
    pathname.startsWith("/store/")
  ) {
    return "shop";
  }
  if (
    pathname === "/about" ||
    pathname === "/contact" ||
    pathname === "/shipping" ||
    pathname === "/size-guide" ||
    pathname === "/track-order" ||
    pathname === "/terms" ||
    pathname === "/privacy" ||
    pathname === "/refunds"
  ) {
    return "about";
  }
  return "default";
}

export function footerHighlightMessage(variant: FooterVariant): string | null {
  switch (variant) {
    case "checkout":
      return "Secure checkout · Tracked delivery · 30-day returns on eligible items";
    case "shop":
      return "Premium fit · Limited drops · Easy returns if it's not right";
    case "about":
      return "Questions? We usually reply within 24 hours.";
    default:
      return null;
  }
}
